import { useEffect, useState } from 'react';
import api from '@/lib/api';
import { AlertTriangle, Clock, CheckCircle2, ExternalLink } from 'lucide-react';

const fmt = (v) => {
  if (!v && v !== 0) return '—';
  if (v >= 1e7) return `₹${(v/1e7).toFixed(2).replace(/\.?0+$/, '')}Cr`;
  if (v >= 1e5) return `₹${(v/1e5).toFixed(1)}L`;
  return `₹${Math.round(v).toLocaleString('en-IN')}`;
};

/**
 * First-Invoice SLA tracker card.
 *
 * Shows every Closed Won deal that is past `threshold_days` without a single
 * b2b_revenue entry. Designed for the CEO dashboard sidebar and for division
 * Leaders (Srikanth, Nisha) to see "sign and forget" deals at a glance.
 *
 * Props:
 *   divisionId  - scope to one contract division (lc / cc-clubs / cc-ops / cs).
 *                 Omit to show across all contract divisions (CEO view).
 *   thresholdDays (default 30)
 *   variant: 'card' | 'inline'  - card is a standalone white card, inline skips chrome
 */
export default function InvoiceSLATracker({ divisionId = null, thresholdDays = 30, variant = 'card' }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const params = new URLSearchParams();
    if (divisionId) params.set('division_id', divisionId);
    if (thresholdDays) params.set('threshold_days', String(thresholdDays));
    (async () => {
      setLoading(true);
      try {
        const r = await api.get(`/b2b-revenue/invoice-sla?${params.toString()}`);
        setData(r.data);
      } catch (_) { setData(null); }
      setLoading(false);
    })();
  }, [divisionId, thresholdDays]);

  if (loading || !data) return null;
  const summary = data.summary || {};
  // Non-contract division → endpoint returns empty + note.
  if (summary.note) return null;

  const breached = (data.deals || []).filter(d => d.status !== 'green');
  const wrapperCls = variant === 'card'
    ? 'rounded-xl border p-4 bg-white'
    : '';
  const wrapperStyle = variant === 'card' ? { borderColor: '#E5E1DC' } : {};

  return (
    <div className={wrapperCls} style={wrapperStyle} data-testid="invoice-sla-tracker">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ backgroundColor: breached.length > 0 ? '#FEE2E2' : '#DCFCE7' }}>
            {breached.length > 0 ? <AlertTriangle size={14} style={{ color: '#DC2626' }} /> : <CheckCircle2 size={14} style={{ color: '#16A34A' }} />}
          </div>
          <div>
            <p className="text-sm font-semibold" style={{ color: '#1B2A4A' }}>First-Invoice SLA</p>
            <p className="text-[11px]" style={{ color: '#6B7280' }}>
              {breached.length === 0
                ? `All ${summary.total} Closed Won deals within ${thresholdDays}-day SLA ✓`
                : `${breached.length} of ${summary.total} deals past ${thresholdDays}-day SLA`}
            </p>
          </div>
        </div>
        {breached.length > 0 && (
          <div className="flex items-center gap-2">
            {summary.breached_red > 0 && (
              <span className="text-[10px] font-bold px-2 py-1 rounded-full" style={{ backgroundColor: '#FEE2E2', color: '#991B1B' }} data-testid="sla-red-count">
                {summary.breached_red} red (&gt;{thresholdDays * 2}d)
              </span>
            )}
            {summary.breached_amber > 0 && (
              <span className="text-[10px] font-bold px-2 py-1 rounded-full" style={{ backgroundColor: '#FEF3C7', color: '#92400E' }} data-testid="sla-amber-count">
                {summary.breached_amber} amber
              </span>
            )}
          </div>
        )}
      </div>

      {breached.length > 0 && (
        <div className="space-y-1.5" data-testid="sla-breach-list">
          {breached.slice(0, 6).map((d) => {
            const badge = d.status === 'red' ? { bg: '#FEE2E2', fg: '#991B1B' } : { bg: '#FEF3C7', fg: '#92400E' };
            return (
              <div key={d.deal_id} className="flex items-center justify-between gap-2 py-1.5 border-b" style={{ borderColor: '#F3F4F6' }} data-testid={`sla-breach-${d.deal_id}`}>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium truncate" style={{ color: '#1B2A4A' }}>{d.client_name}</p>
                  <p className="text-[10px]" style={{ color: '#6B7280' }}>
                    {d.entity?.toUpperCase() || '—'} · {fmt(d.deal_value)} · {d.owner || 'unassigned'}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full" style={{ backgroundColor: badge.bg, color: badge.fg }}>
                    <Clock size={9} className="inline -mt-0.5 mr-0.5" />
                    {d.days_since_close}d
                  </span>
                </div>
              </div>
            );
          })}
          {breached.length > 6 && (
            <p className="text-[10px] text-center pt-1" style={{ color: '#6B7280' }}>+{breached.length - 6} more</p>
          )}
        </div>
      )}

      {breached.length === 0 && summary.total > 0 && (
        <p className="text-[11px] italic" style={{ color: '#6B7280' }}>
          <CheckCircle2 size={11} className="inline -mt-0.5" /> No "sign-and-forget" deals detected.
        </p>
      )}
    </div>
  );
}
